/* =============================================================
   ConsultationRequestPage — /consultation-request
   Standalone form: name, phone, email, country, visa category,
   travel timeline, preferred call time → saved to DB + owner notified
   ============================================================= */
import { useState } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, User, Phone, Mail, Globe, FileText,
  Calendar, Clock, CheckCircle2, ChevronDown, Send, MessageSquareText,
} from "lucide-react";
import Header from "@/components/Header";
import { trpc } from "@/lib/trpc";

/* ── Static data ─────────────────────────────────────────────── */
const COUNTRIES = [
  { value: "usa",         label: "🇺🇸 АНУ" },
  { value: "australia",   label: "🇦🇺 Австрали" },
  { value: "canada",      label: "🇨🇦 Канад" },
  { value: "ireland",     label: "🇮🇪 Ирланд" },
  { value: "uk",          label: "🇬🇧 Их Британи" },
  { value: "schengen",    label: "🇪🇺 Шенген" },
  { value: "czech",       label: "🇨🇿 Чех" },
  { value: "newzealand",  label: "🇳🇿 Шинэ Зеланд" },
  { value: "other",       label: "🌍 Бусад" },
];

const VISA_CATEGORIES_BY_COUNTRY: Record<string, string[]> = {
  usa:        ["Аяллын виз (B1/B2)", "Сургалтын виз (F-1)", "АНУ-ын ахлах сургуулийн сурагч солилцоо", "Work and Travel", "2+2 — 16 наснаас ахлах сургуульд үргэлжлүүлэн дээд сургуульд суралцах хөтөлбөр", "Ажлын виз / Ногоон карт (EB3/NIW/H1B)", "Цагаачлал"],
  australia:  ["Аяллын виз", "Сургалтын виз", "Цагаачлал / Ажлын виз"],
  canada:     ["Аяллын виз", "Сургалтын виз", "Цагаачлал (Express Entry)"],
  ireland:    ["Аяллын виз", "Сургалт + Ажил"],
  uk:         ["Аяллын виз", "Сургалтын виз"],
  schengen:   ["Аяллын виз"],
  czech:      ["Хөдөлмөрийн гэрээ"],
  newzealand: ["Мэргэжлээрээ ажиллах", "Аяллын виз", "Сургалтын виз"],
  other:      ["Аяллын виз", "Сургалтын виз", "Ажлын виз", "Бусад"],
};

const CONSULTATION_LOCATIONS = [
  { value: "mongolia", label: "🇲🇳 Монгол" },
  { value: "usa", label: "🇺🇸 АНУ" },
  { value: "australia", label: "🇦🇺 Австрали" },
  { value: "south-korea", label: "🇰🇷 Солонгос" },
  { value: "japan", label: "🇯🇵 Япон" },
  { value: "eu", label: "🇪🇺 Европын холбооны улс" },
  { value: "uk", label: "🇬🇧 Англи, Их Британи" },
  { value: "ireland", label: "🇮🇪 Ирланд" },
  { value: "other", label: "🌍 Бусад" },
];

const CONTACT_APPS = [
  { value: "whatsapp", label: "WhatsApp" },
  { value: "viber", label: "Viber" },
  { value: "telegram", label: "Telegram" },
] as const;

const TIMELINES = [
  "1 сарын дотор",
  "1–3 сарын дотор",
  "3–6 сарын дотор",
  "6 сараас дээш",
  "Одоохондоо тодорхойгүй",
];

const CALL_TIMES = [
  { value: "now",       label: "Яг одоо",        icon: "⚡" },
  { value: "morning",   label: "Өглөө (09:00–12:00)", icon: "🌅" },
  { value: "afternoon", label: "Үдээс хойш (13:00–18:00)", icon: "☀️" },
];

/* ── Form state type ─────────────────────────────────────────── */
interface FormState {
  name: string;
  phone: string;
  email: string;
  country: string;
  visaCategory: string;
  consultationLocation: string;
  requestNote: string;
  contactApp: "" | "whatsapp" | "viber" | "telegram";
  contactAppNumber: string;
  travelTimeline: string;
  callTime: "now" | "morning" | "afternoon" | "";
}

/* ── Field wrapper ───────────────────────────────────────────── */
function Field({ label, icon, children }: { label: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="flex items-center gap-2 text-white/60 text-xs font-semibold uppercase tracking-wide">
        <span className="text-white/40">{icon}</span>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-xl text-white text-sm placeholder-white/25 outline-none transition-all focus:ring-1";
const inputStyle = {
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.10)",
};
const inputFocusStyle = {
  borderColor: "rgba(139,92,246,0.50)",
  boxShadow: "0 0 0 3px rgba(139,92,246,0.12)",
};

/* ── Main component ──────────────────────────────────────────── */
export default function ConsultationRequestPage() {
  const [, navigate] = useLocation();
  const [step, setStep] = useState<"form" | "success">("form");
  const [focused, setFocused] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>({
    name: "", phone: "", email: "",
    country: "", visaCategory: "",
    consultationLocation: "mongolia", requestNote: "",
    contactApp: "", contactAppNumber: "",
    travelTimeline: "", callTime: "",
  });

  const set = (k: keyof FormState, v: string) =>
    setForm(prev => ({ ...prev, [k]: v, ...(k === "country" ? { visaCategory: "" } : {}) }));

  const visaOptions = form.country ? VISA_CATEGORIES_BY_COUNTRY[form.country] ?? [] : [];
  const needsInternationalMessenger = form.consultationLocation !== "mongolia";

  const mutation = trpc.consultation.submitRequest.useMutation({
    onSuccess: () => setStep("success"),
  });

  const isValid =
    form.name.trim() &&
    form.phone.trim() &&
    form.country &&
    form.visaCategory &&
    form.travelTimeline &&
    form.callTime &&
    (!needsInternationalMessenger || (form.contactApp && form.contactAppNumber.trim()));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || mutation.isPending) return;
    mutation.mutate({
      name: form.name.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      country: form.country,
      visaCategory: form.visaCategory,
      consultationLocation: CONSULTATION_LOCATIONS.find(item => item.value === form.consultationLocation)?.label ?? "Монгол",
      requestNote: form.requestNote.trim(),
      contactApp: needsInternationalMessenger && form.contactApp ? form.contactApp : undefined,
      contactAppNumber: needsInternationalMessenger ? form.contactAppNumber.trim() : undefined,
      travelTimeline: form.travelTimeline,
      callTime: form.callTime as "now" | "morning" | "afternoon",
    });
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 45%, #0F0B35 100%)" }}
    >
      <Header />

      <div className="container pt-24 pb-16 max-w-lg mx-auto">
        {/* Back */}
        <motion.button
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/")}
          className="flex items-center gap-2 mb-8 text-white/50 hover:text-white/80 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Буцах</span>
        </motion.button>

        <AnimatePresence mode="wait">
          {step === "success" ? (
            /* ── Success state ── */
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center text-center py-16 gap-5"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                className="w-20 h-20 rounded-full flex items-center justify-center"
                style={{ background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)" }}
              >
                <CheckCircle2 className="w-10 h-10 text-white" />
              </motion.div>
              <div>
                <h2
                  className="text-white font-black text-2xl mb-2"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  Хүсэлт амжилттай илгээгдлээ!
                </h2>
                <p className="text-white/55 text-sm leading-relaxed max-w-xs mx-auto">
                  Бид таны мэдээллийг аваад, таньтай удахгүй холбогдох болно.
                </p>
              </div>
              <button
                onClick={() => navigate("/")}
                className="mt-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition-all"
                style={{
                  background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
                  color: "white",
                  boxShadow: "0 4px 16px rgba(124,58,237,0.35)",
                }}
              >
                Нүүр хуудас руу буцах
              </button>
            </motion.div>
          ) : (
            /* ── Form ── */
            <motion.div key="form" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              {/* Header */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)" }}
                  >
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h1
                      className="text-white font-black text-2xl leading-tight"
                      style={{ fontFamily: "'Sora', sans-serif", letterSpacing: "-0.02em" }}
                    >
                      Зөвлөгөө авах хүсэлт
                    </h1>
                    <p className="text-white/45 text-sm mt-0.5">
                      Мэдээллээ бөглөөрэй. Бид таньтай холбогдох болно.
                    </p>
                  </div>
                </div>

                {/* Info chips */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {["Монгол хэлээр", "Мэргэжлийн зөвлөх"].map((t) => (
                    <span
                      key={t}
                      className="px-3 py-1 rounded-full text-xs"
                      style={{ background: "rgba(124,58,237,0.12)", color: "rgba(167,139,250,0.9)" }}
                    >
                      ✓ {t}
                    </span>
                  ))}
                </div>
              </div>

              {/* Contact expectation */}
              <div
                className="rounded-2xl p-4 mb-2"
                style={{ background: "rgba(124,58,237,0.10)", border: "1px solid rgba(124,58,237,0.20)" }}
              >
                <p className="text-white/80 text-sm leading-relaxed">
                  ✅ Бид таны мэдээллийг аваад, таньтай холбогдох болно.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Name */}
                <Field label="Таны нэр" icon={<User className="w-3.5 h-3.5" />}>
                  <input
                    type="text"
                    placeholder="Бат-Эрдэнэ"
                    value={form.name}
                    onChange={e => set("name", e.target.value)}
                    onFocus={() => setFocused("name")}
                    onBlur={() => setFocused(null)}
                    className={inputCls}
                    style={{ ...inputStyle, ...(focused === "name" ? inputFocusStyle : {}) }}
                    required
                  />
                </Field>

                {/* Phone */}
                <Field label="Утасны дугаар" icon={<Phone className="w-3.5 h-3.5" />}>
                  <input
                    type="tel"
                    placeholder="+976 9900 0000"
                    value={form.phone}
                    onChange={e => set("phone", e.target.value)}
                    onFocus={() => setFocused("phone")}
                    onBlur={() => setFocused(null)}
                    className={inputCls}
                    style={{ ...inputStyle, ...(focused === "phone" ? inputFocusStyle : {}) }}
                    required
                  />
                </Field>

                {/* Email */}
                <Field label="Имэйл хаяг (сонголтоор)" icon={<Mail className="w-3.5 h-3.5" />}>
                  <input
                    type="email"
                    placeholder="example@gmail.com"
                    value={form.email}
                    onChange={e => set("email", e.target.value)}
                    onFocus={() => setFocused("email")}
                    onBlur={() => setFocused(null)}
                    className={inputCls}
                    style={{ ...inputStyle, ...(focused === "email" ? inputFocusStyle : {}) }}
                  />
                </Field>

                {/* Current location */}
                <Field label="Та хаанаас зөвлөгөө авч байна вэ?" icon={<Globe className="w-3.5 h-3.5" />}>
                  <div className="relative">
                    <select
                      value={form.consultationLocation}
                      onChange={e => {
                        const consultationLocation = e.target.value;
                        setForm(previous => ({
                          ...previous,
                          consultationLocation,
                          ...(consultationLocation === "mongolia" ? { contactApp: "", contactAppNumber: "" } : {}),
                        }));
                      }}
                      className={inputCls + " appearance-none pr-10 cursor-pointer"}
                      style={{ ...inputStyle, ...(focused === "consultationLocation" ? inputFocusStyle : {}), color: "white" }}
                      onFocus={() => setFocused("consultationLocation")}
                      onBlur={() => setFocused(null)}
                    >
                      {CONSULTATION_LOCATIONS.map(location => (
                        <option key={location.value} value={location.value} style={{ background: "#130D3A", color: "white" }}>
                          {location.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                  </div>
                </Field>

                <AnimatePresence>
                  {needsInternationalMessenger && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <Field label="Холбогдох апп сонгоно уу" icon={<Phone className="w-3.5 h-3.5" />}>
                        <p className="mb-1 text-xs leading-relaxed text-sky-100/60">Монголоос өөр улсад байгаа тул WhatsApp, Viber эсвэл Telegram-ийн аль нэг дугаараа оруулна уу.</p>
                        <div className="grid grid-cols-3 gap-2">
                          {CONTACT_APPS.map(app => {
                            const active = form.contactApp === app.value;
                            return (
                              <button
                                key={app.value}
                                type="button"
                                onClick={() => set("contactApp", app.value)}
                                className="rounded-xl px-2 py-3 text-xs font-bold transition-all"
                                style={{
                                  background: active ? "rgba(56,189,248,0.18)" : "rgba(255,255,255,0.04)",
                                  border: active ? "1px solid rgba(56,189,248,0.60)" : "1px solid rgba(255,255,255,0.08)",
                                  color: active ? "#bae6fd" : "rgba(255,255,255,0.60)",
                                }}
                              >
                                {active && <span className="mr-1">✓</span>}
                                {app.label}
                              </button>
                            );
                          })}
                        </div>
                        {form.contactApp && (
                          <input
                            type="tel"
                            value={form.contactAppNumber}
                            onChange={e => set("contactAppNumber", e.target.value)}
                            onFocus={() => setFocused("contactAppNumber")}
                            onBlur={() => setFocused(null)}
                            placeholder={`${CONTACT_APPS.find(app => app.value === form.contactApp)?.label} дугаар (+улсын кодтой)`}
                            className={inputCls + " mt-2"}
                            style={{ ...inputStyle, ...(focused === "contactAppNumber" ? inputFocusStyle : {}) }}
                            required
                          />
                        )}
                      </Field>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Country */}
                <Field label="Явах улс" icon={<Globe className="w-3.5 h-3.5" />}>
                  <div className="relative">
                    <select
                      value={form.country}
                      onChange={e => set("country", e.target.value)}
                      className={inputCls + " appearance-none pr-10 cursor-pointer"}
                      style={{ ...inputStyle, ...(focused === "country" ? inputFocusStyle : {}), color: form.country ? "white" : "rgba(255,255,255,0.25)" }}
                      onFocus={() => setFocused("country")}
                      onBlur={() => setFocused(null)}
                      required
                    >
                      <option value="" disabled>Улс сонгоно уу</option>
                      {COUNTRIES.map(c => (
                        <option key={c.value} value={c.value} style={{ background: "#130D3A", color: "white" }}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30 pointer-events-none" />
                  </div>
                </Field>

                {/* Visa category — shown only after country selected */}
                <AnimatePresence>
                  {form.country && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <Field label="Визний ангилал" icon={<FileText className="w-3.5 h-3.5" />}>
                        <div className="grid grid-cols-1 gap-2">
                          {visaOptions.map(opt => (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => set("visaCategory", opt)}
                              className="text-left px-4 py-3 rounded-xl text-sm transition-all"
                              style={{
                                background: form.visaCategory === opt
                                  ? "rgba(124,58,237,0.20)"
                                  : "rgba(255,255,255,0.04)",
                                border: form.visaCategory === opt
                                  ? "1px solid rgba(124,58,237,0.50)"
                                  : "1px solid rgba(255,255,255,0.08)",
                                color: form.visaCategory === opt ? "white" : "rgba(255,255,255,0.60)",
                              }}
                            >
                              {form.visaCategory === opt && (
                                <span className="mr-2 text-violet-400">✓</span>
                              )}
                              {opt}
                            </button>
                          ))}
                        </div>
                      </Field>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Travel timeline */}
                <Field label="Хэр ойрын хугацаанд явах хүсэлтэй вэ?" icon={<Calendar className="w-3.5 h-3.5" />}>
                  <div className="grid grid-cols-1 gap-2">
                    {TIMELINES.map(t => (
                      <button
                        key={t}
                        type="button"
                        onClick={() => set("travelTimeline", t)}
                        className="text-left px-4 py-3 rounded-xl text-sm transition-all"
                        style={{
                          background: form.travelTimeline === t
                            ? "rgba(52,211,153,0.12)"
                            : "rgba(255,255,255,0.04)",
                          border: form.travelTimeline === t
                            ? "1px solid rgba(52,211,153,0.40)"
                            : "1px solid rgba(255,255,255,0.08)",
                          color: form.travelTimeline === t ? "#34d399" : "rgba(255,255,255,0.60)",
                        }}
                      >
                        {form.travelTimeline === t && <span className="mr-2">✓</span>}
                        {t}
                      </button>
                    ))}
                  </div>
                </Field>

                {/* Call time */}
                <Field label="Залгаж болох хугацаа" icon={<Clock className="w-3.5 h-3.5" />}>
                  <div className="grid grid-cols-3 gap-2">
                    {CALL_TIMES.map(ct => (
                      <button
                        key={ct.value}
                        type="button"
                        onClick={() => set("callTime", ct.value)}
                        className="flex flex-col items-center gap-1.5 px-3 py-3 rounded-xl text-xs font-medium transition-all"
                        style={{
                          background: form.callTime === ct.value
                            ? "rgba(129,140,248,0.18)"
                            : "rgba(255,255,255,0.04)",
                          border: form.callTime === ct.value
                            ? "1px solid rgba(129,140,248,0.50)"
                            : "1px solid rgba(255,255,255,0.08)",
                          color: form.callTime === ct.value ? "#818cf8" : "rgba(255,255,255,0.55)",
                        }}
                      >
                        <span className="text-xl">{ct.icon}</span>
                        <span className="text-center leading-tight">{ct.label}</span>
                      </button>
                    ))}
                  </div>
                </Field>

                {/* Client request note */}
                <Field label="Та өөрийн хүсэлт, асуултаа бичнэ үү" icon={<MessageSquareText className="w-3.5 h-3.5" />}>
                  <textarea
                    value={form.requestNote}
                    onChange={e => set("requestNote", e.target.value)}
                    onFocus={() => setFocused("requestNote")}
                    onBlur={() => setFocused(null)}
                    placeholder="Жишээ: Хүүхдийнхээ сургалтын боломж, визний материалын талаар зөвлөгөө авах хүсэлтэй байна."
                    rows={4}
                    className={inputCls + " min-h-28 resize-y"}
                    style={{ ...inputStyle, ...(focused === "requestNote" ? inputFocusStyle : {}) }}
                  />
                </Field>

                {/* Submit */}
                <motion.button
                  type="submit"
                  disabled={!isValid || mutation.isPending}
                  whileTap={{ scale: 0.98 }}
                  className="flex items-center justify-center gap-2 w-full py-4 rounded-xl text-base font-bold transition-all mt-2"
                  style={{
                    background: isValid
                      ? "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)"
                      : "rgba(255,255,255,0.06)",
                    color: isValid ? "white" : "rgba(255,255,255,0.30)",
                    boxShadow: isValid ? "0 4px 20px rgba(124,58,237,0.40)" : "none",
                    cursor: isValid ? "pointer" : "not-allowed",
                  }}
                >
                  {mutation.isPending ? (
                    <span className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Хүсэлт илгээх
                    </>
                  )}
                </motion.button>

                {mutation.isError && (
                  <p className="text-red-400 text-sm text-center">
                    {mutation.error.message || "Алдаа гарлаа. Дахин оролдоно уу."}
                  </p>
                )}

                <p className="text-white/30 text-xs text-center leading-relaxed">
                  Таны мэдээлэл зөвхөн зөвлөгөө өгөх зорилгоор ашиглагдах бөгөөд гуравдагч этгээдэд дамжуулагдахгүй.
                </p>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
